<template>
  <div id="map-beige-container" class="map">
    <!-- custom popup component -->
    <Popup />

    <!-- legend -->
    <LegendGRP v-if="currentPage === 3" v-show="step === '3-3'" />

    <!-- timeline player -->
    <TimelinePlayer v-if="currentPage === 3" />
  </div>
</template>

<script>
import * as d3 from 'd3';
import { mapGetters } from 'vuex';

import mapServices from '@/services/map';

// components
import Popup from '@/components/ui/map/Popup.vue';
import LegendGRP from '@/components/ui/map/LegendGRP.vue';
import TimelinePlayer from '@/components/ui/map/TimelinePlayer.vue';

export default {
  name: 'MapBeige',
  components: {
    Popup,
    LegendGRP,
    TimelinePlayer,
  },
  data() {
    return {
      map: null,
      defaultOptions: {
        container: 'map-beige-container',
        style: 'mapbox://styles/kontinentalist/cjlq3un8l1lxo2rp5o8ixlqf4',
        center: [88.9, 37.4],
        zoom: 1.82,
      },
    };
  },
  computed: {
    ...mapGetters('view', ['currentPage', 'step']),
    ...mapGetters('viewTimeline', ['currentYear']),
    ...mapGetters('mapState', ['mapOptions', 'mapLayers']),
    ...mapGetters('popup', ['popup']),
  },
  watch: {
    currentPage(newVal, oldVal) {
      this.handleMap(newVal, oldVal);
    },
    step(newVal, oldVal) {
      this.handleMapEvents(newVal, oldVal);
    },
    currentYear(newVal) {
      this.loadView3Layers(newVal, true);
    },
  },
  mounted() {
    // init Mapbox
    this.map = mapServices.createMap(this.defaultOptions);
  },
  methods: {
    handleMap(currentPage, previousPage) {
      // clean up of map
      this.cleanMap(previousPage);

      // update map to page view
      switch (currentPage) {
        case 2:
          this.updateMapCorridors();
          break;

        case 3:
          this.updateMapGRP();
          this.loadView3Layers();
          break;

        default:
          break;
      }
    },
    cleanMap(page) {
      switch (page) {
        case 2:
          this.map.removeLayer('corridors');
          this.map.removeSource('corridors');
          break;
        case 3:
          // clean up map
          this.map.removeLayer('GRP');
          this.map.removeLayer('population');

          this.map.removeSource('GRP');
          this.map.removeSource('population');
          break;

        default:
          break;
      }
    },
    updateMapCorridors() {
      // load view 2
      // camera
      const options = this.mapOptions(2);
      this.map.flyTo(options);

      // geojson data
      this.map.addSource('corridors', {
        type: 'geojson',
        data: '/data/map/corridors/belt-road.json',
      });

      // layers
      const layers = this.mapLayers(2);
      this.map.addLayer(layers[0], 'state-label-sm');
    },
    updateMapGRP() {
      // load view 3
      const options = this.mapOptions(3);
      this.map.flyTo(options);
    },
    handleMapEvents(currentStep, previousStep) {
      // update map events of view's steps
      switch (currentStep) {
        case '2-3':
          this.updateMapCorridorsEvents(true);
          break;

        case '3-1':
          this.map.flyTo({
            center: [114.865454, 31.775887],
            zoom: 4.84,
            bearing: -37.6,
            pitch: 60,
          });
          break;

        case '3-3':
          this.updateMapGRPEvents(true);
          break;

        default:
          break;
      }

      switch (previousStep) {
        case '2-3':
          this.updateMapCorridorsEvents(false);
          break;

        case '3-1':
          this.map.flyTo({
            center: [104.2, 37.37],
            zoom: 3.79,
            bearing: 0,
            pitch: 0,
          });
          break;

        case '3-3':
          this.updateMapGRPEvents(false);
          break;

        default:
          break;
      }
    },
    updateMapCorridorsEvents(active) {
      // add events
      if (active) {
        // enable dragPan
        this.map.dragPan.enable();

        // cursor drag, dragging
        d3.select(this.map.getCanvas().parentElement).classed('mapboxgl-interactive', true);

        // Change cursor when hover
        this.map.on('mousemove', 'corridors', this.handleMapPopup);
        this.map.on('mouseleave', 'corridors', this.handleLayerExit);
      }
      // remove events
      else {
        // disable dragPan
        this.map.dragPan.disable();

        // hide UI
        d3.select('.mapboxgl-custom-popup').style('display', 'none');

        // remove mouse events
        d3.select(this.map.getCanvas().parentElement).classed('mapboxgl-interactive', false);

        this.map.off('mousemove', 'corridors', this.handleMapPopup);
        this.map.off('mouseleave', 'corridors', this.handleLayerExit);
      }
    },
    updateMapGRPEvents(active) {
      // add events
      if (active) {
        // enable dragPan
        this.map.dragPan.enable();

        // cursor drag, dragging
        d3.select(this.map.getCanvas().parentElement).classed('mapboxgl-interactive', true);

        // show UI
        d3.select('#map-beige-container .mapboxgl-custom-legend').style('display', 'block');
        d3.select('.mapboxgl-custom-timeline').style('display', 'block');

        // Change cursor when hover
        this.map.on('mousemove', 'GRP', this.handleMapPopup);
        this.map.on('mouseleave', 'GRP', this.handleLayerExit);
        this.map.on('mousemove', 'population', this.handleMapPopup);
        this.map.on('mouseleave', 'population', this.handleLayerExit);
      } else {
        // disable dragPan
        this.map.dragPan.disable();

        // hide UI
        d3.select('.mapboxgl-custom-popup').style('display', 'none');
        d3.select('#map-beige-container .mapboxgl-custom-legend').style('display', 'none');
        d3.select('.mapboxgl-custom-timeline').style('display', 'none');

        // remove mouse events
        d3.select(this.map.getCanvas().parentElement).classed('mapboxgl-interactive', false);

        this.map.off('mousemove', 'GRP', this.handleMapPopup);
        this.map.off('mouseleave', 'GRP', this.handleLayerExit);
        this.map.off('mousemove', 'population', this.handleMapPopup);
        this.map.off('mouseleave', 'population', this.handleLayerExit);
      }
    },
    handleMapPopup(e) {
      this.handleLayerEnter();

      // the popup element
      const popupEl = d3.select('.mapboxgl-custom-popup');

      // popup content
      let title = '';
      let description = '';
      let color = '';
      let grpYear = '';
      let popYear = '';

      switch (e.features[0].layer.id) {
        case 'corridors':
          title = e.features[0].properties.name;
          description = e.features[0].properties.description;
          color = e.features[0].properties.color;

          popupEl
            .select('#popup-title')
            .classed('corridor-title', true)
            .style('border-left-color', color);
          break;

        case 'GRP':
          title = e.features[0].properties.NAME_1;
          // convert year property
          grpYear = this.currentYear === 2011 ? 'year2012' : `year${this.currentYear}`;
          description = `GRP: ${e.features[0].properties[grpYear]}`;

          popupEl
            .select('#popup-title')
            .classed('corridor-title', false)
            .attr('style', '');
          break;

        case 'population':
          title = e.features[0].properties.region;
          // convert year property
          popYear = this.currentYear === 2011 ? 'year2012' : `year${this.currentYear}`;
          description = `Population: ${e.features[0].properties[popYear]}`;

          popupEl
            .select('#popup-title')
            .classed('corridor-title', false)
            .attr('style', '');
          break;

        default:
          break;
      }

      // update popup el
      popupEl.style('display', 'block').style('transform', `translate(${e.point.x}px, ${e.point.y}px)`);

      // update popup content
      this.$store.dispatch('popup/updateTitle', title);
      this.$store.dispatch('popup/updateDescription', description);
    },
    loadView3Layers(year = 2012, filter = false) {
      if (!filter) {
        // add source and layers
        // source geojson
        this.map.addSource('GRP', {
          type: 'geojson',
          data: '/data/map/chinaGRP/GRP.json',
        });
        this.map.addSource('population', {
          type: 'geojson',
          data: '/data/map/chinaGRP/population.json',
        });

        // layers
        const layers = this.mapLayers(3);
        let layerGRP = layers.find(layer => layer.id === 'GRP');
        let layerPop = layers.find(layer => layer.id === 'population');

        this.map.addLayer(layerGRP, 'state-label-sm');
        this.map.addLayer(layerPop, 'state-label-sm');
      } else {
        // convert year property
        let propYear = year === 2011 ? 'year2012' : `year${year}`;

        // filter map layers paint props via timeline year
        this.map.setPaintProperty('GRP', 'fill-color', [
          'interpolate',
          ['linear'],
          ['get', propYear],
          441.36,
          '#c4cced',
          19962,
          '#766eb4',
          39482,
          '#4c3e89',
        ]);
        this.map.setPaintProperty('population', 'circle-radius', ['round', ['/', ['sqrt', ['get', propYear]], 2]]);
      }
    },
    handleLayerEnter() {
      this.map.getCanvas().style.cursor = 'pointer';
    },
    handleLayerExit() {
      this.map.getCanvas().style.cursor = '';
      d3.select('.mapboxgl-custom-popup').style('display', 'none');
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~mapbox-gl/dist/mapbox-gl.css';
</style>
